import { useState, type FC } from 'react';
import { Link } from 'react-router-dom';
import { Settings, Bell, User } from 'lucide-react';
import { useUnreadNotificationCount } from '../../hooks/useNotifications';
import { ThemeToggle } from '../theme';

export interface DashboardHeaderProps {
  user?: {
    name?: string;
    email?: string;
    avatar?: string;
  };
  onNotificationsClick?: () => void;
}

export const DashboardHeader: FC<DashboardHeaderProps> = ({ user, onNotificationsClick }) => {
  const firstName = user?.name?.split(' ')[0] || 'there';
  const greeting = `Welcome back, ${firstName}!`;
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const [avatarError, setAvatarError] = useState(false);

  return (
    <header className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 sticky top-0 z-10">
      <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-4">
        {/* Greeting */}
        <p className="text-lg font-semibold text-gray-900 dark:text-slate-100 lg:hidden ml-12">
          {greeting}
        </p>
        <p className="text-lg font-semibold text-gray-900 dark:text-slate-100 hidden lg:block">
          {greeting}
        </p>

        {/* User Menu */}
        <div className="flex items-center space-x-4">
          <ThemeToggle />

          <button
            data-testid="notification-bell"
            onClick={onNotificationsClick}
            className="relative p-2 text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs font-medium bg-red-500 text-white rounded-full">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          <Link
            to="/settings"
            className="p-2 text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            aria-label="Settings"
          >
            <Settings className="w-5 h-5" />
          </Link>

          <div className="flex items-center space-x-3 pl-4 border-l border-gray-200 dark:border-slate-700">
            {user?.avatar && !avatarError ? (
              <img
                src={user.avatar}
                alt={user?.name || 'User avatar'}
                className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-200 dark:ring-slate-600"
                onError={() => setAvatarError(true)}
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-slate-700 ring-2 ring-gray-200 dark:ring-slate-600 flex items-center justify-center">
                <User className="w-5 h-5 text-gray-500 dark:text-slate-400" />
              </div>
            )}
            <div className="hidden lg:block text-left">
              <p className="text-sm font-medium text-gray-900 dark:text-slate-100">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-gray-500 dark:text-slate-400">{user?.email || ''}</p>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default DashboardHeader;
